import React from "react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { getMonthlyData, getCategoryData } from "../data/mockData";
import { formatCurrency } from "../utils/helpers";

const CustomTooltip = ({ active, payload, label, dark }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: dark ? "#1e2a3a" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
      borderRadius: 10, padding: 
      "12px 16px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    }}>
      <p style={{ margin: "0 0 8px", color: dark ? "#94a3b8" : "#64748b", fontSize: 12 }}>{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ margin: "3px 0", color: p.color, fontSize: 13, fontWeight: 600 }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

export function TrendChart({ transactions, theme }) {
  const dark = theme === "dark";
  const data = getMonthlyData(transactions);
  const textColor = dark ? "#475569" : "#94a3b8";
  const gridColor = dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)";

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={data}
       margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#f43f5e" stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 
        3" stroke={gridColor} />
        <XAxis dataKey="month" stroke={textColor} tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis stroke={textColor} tick={{ fill: textColor, fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
        <Tooltip content={<CustomTooltip dark={dark} />} />
        <Legend wrapperStyle={{ color: textColor, fontSize: 12 }} />
        <Area type="monotone" 
        dataKey="income" stroke="#10b981" strokeWidth={2.5} fill="url(#incomeGrad)" name="Income" dot={{ r: 4, fill: "#10b981", strokeWidth: 0 }} activeDot={{ r: 6 }} />
        <Area type="monotone" dataKey="expense" stroke="#f43f5e" strokeWidth={2.5} fill="url(#expenseGrad)" name="Expense" dot={{ r: 4, fill: "#f43f5e", strokeWidth: 0 }} activeDot={{ r: 6 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function BalanceChart({ transactions, theme }) {
  const dark = theme === "dark";
  const data = getMonthlyData(transactions);
  const textColor = dark ? "#475569" : "#94a3b8";
  const gridColor = dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.06)";

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid 
        strokeDasharray="3 3" stroke={gridColor} />
        <XAxis dataKey="month" stroke={textColor} tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis stroke={textColor}
         tick={{ fill: textColor, fontSize: 11 }} axisLine={false} tickLine={false}
          tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
        <Tooltip content={<CustomTooltip dark={dark} />} />
        <Legend wrapperStyle={{ color: textColor, 
          fontSize: 12 }} />
        <Bar dataKey="income" fill="#10b981" name="Income" radius={[4, 4, 0, 0]} maxBarSize={40} />
        <Bar dataKey="expense" fill="#f43f5e" 
        
        name="Expense" radius={[4, 4, 0, 0]} maxBarSize={40} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function CategoryChart({ transactions, theme }) {
  const dark = theme === "dark";
  const data = getCategoryData(transactions);
  const textColor = dark ? "#94a3b8" : "#64748b";

  const CustomLabel = ({ cx, cy, midAngle,
     innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const r = innerRadius + 
    (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>{`${(percent * 100).toFixed(0)}%`}</text>;
  };

  return (
    <div style={{ display: 
    "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
      <ResponsiveContainer width={220} height={220} style={{ flexShrink: 0 }}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={100} paddingAngle={2} dataKey="value" labelLine={false} label={CustomLabel}>
            {data.map((entry) => 
            <Cell key={entry.name} fill={entry.color} />)}
          </Pie>
          <Tooltip formatter={(v) => formatCurrency(v)} contentStyle={{ background: dark ? "#1e2a3a" : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, borderRadius: 8 }} labelStyle={{ color: dark ? "#94a3b8" : "#64748b" }} />
        </PieChart>
      </ResponsiveContainer>
      <div style={{ flex: 1, minWidth: 180 }}>
        {data.map((item) => (
          <div key={item.name} style={{
             display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ display: "flex", 
              alignItems: "center", gap: 8 }}>
              <div style={{ width: 10, height: 
                10, borderRadius: 3, background: item.color, flexShrink: 0 }} />
              <span style={{ color: textColor, 
                fontSize: 13 }}>{item.name}</span>
            </div>
            <span style={{ color: dark ? "#f1f5f9" 
              : "#0f172a", fontSize: 13, fontWeight: 600 }}>
                {formatCurrency(item.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
